"use client";

import CommonCard from "@/components/commonCard/CommonCard";
import FeaturedSlider from "@/components/featuredSlider/FeaturedSlider";
import Sidebar, { SideBarTopCard } from "@/components/sideBar/SideBar";
import { useState, useEffect } from "react";
import { Post } from "@/types/post.types";
import BreakingNews from "@/components/breakingNews/BreakingNews";
import AI from "@/components/AI/ai";
import BlogNews from "@/components/BlogNews/blog";
import PressRelease from "@/components/pressRelease/PressRelease";
import FeaturedArticles from "@/components/featuredArticles/FeaturedArticles";
import NewsLetter from "@/components/newsletter/NewsLetter";
import SocialMediaSection from "@/components/socialMediaSection/SocialMediaSection";
import CryptoSlider from "@/components/cryptoSlider/CryptoSlider";
import ScamNews from "@/components/scamNews/ScamNews";
import PostSkeleton from "@/components/skeletons/PostSkeleton";
import SubscribeEmailModal from "@/components/subscribe/SubscribeEmailModal";
import "@/components/scamNews/style.css";
import ExchangeAndWallets from "@/components/exchangeAndWallets/ExchangeAndWallets";
import Subscribe from "../../components/subscribe/subscribe";
import AdSpace from "@/components/ads/Adspace";
import type { SidebarData } from "@/types/sidebar-data";
import type { AdItem } from "@/lib/getAdSpaceData";

// ── Types ────────────────────────────────────────────────────────────────────

interface HomeData {
  sliderPosts: Post[];
  featuredPosts: Post[];
  ai: Post[];
  pressReleases: Post[];
  latestPosts: Post[];
  scamsNews: Post[];
  blogNews: Post[];
}

interface BreakingData {
  breakingNews: Post[];
  breakingNewsCount: number;
}

interface SocialLink {
  key: string;
  name: string;
  url: string;
}

interface HomeClientProps {
  homeData: HomeData;
  breakingData: BreakingData;
  socialLinks: SocialLink[];
  sidebarData: SidebarData;
  initialAds: AdItem[];
}

// ── Static data ───────────────────────────────────────────────────────────────

const PICKS = [
  {
    title: "Bitget",
    imageSrc: "/exchange-and-wallet/Bitget-Logo-1.png",
    link: "#",
  },
  { title: "MEXC", imageSrc: "/exchange-and-wallet/MEXC-Logo.jpg", link: "#" },
  { title: "ByBit", imageSrc: "/exchange-and-wallet/unnamed-2.png", link: "#" },
  {
    title: "Trezor",
    imageSrc: "/exchange-and-wallet/trezor-square.jpg.webp",
    link: "#",
  },
  {
    title: "OKX",
    imageSrc: "/exchange-and-wallet/okx-logo-black-and-white.png",
    link: "#",
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function resolveImage(src: string | null | undefined): string {
  if (!src) return "/image-not-found.png";
  return src.replace(/([^:]\/)\/+/g, "$1");
}

function safeArray<T>(val: T[] | null | undefined): T[] {
  return Array.isArray(val) ? val : [];
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function HomeClient({
  homeData,
  breakingData,
  socialLinks,
  sidebarData,
  initialAds,
}: HomeClientProps) {
  console.log("homeData main", homeData?.blogNews);
  // ── Safe-destructure — guards against undefined props from SSR ─────────────
  const sliderPosts = safeArray(homeData?.sliderPosts);
  const featuredPosts = safeArray(homeData?.featuredPosts);
  const AiNews = safeArray(homeData?.ai);
  const BlogNewsData = safeArray(homeData?.blogNews);
  const PressReleaseNews = safeArray(homeData?.pressReleases);
  const scamsNews = safeArray(homeData?.scamsNews);

  const initialBreaking = safeArray(breakingData?.breakingNews);
  const breakingNewsCount = breakingData?.breakingNewsCount ?? 0;

  // ── State ─────────────────────────────────────────────────────────────────
  const [breakingNews, setBreakingNews] = useState<Post[]>(initialBreaking);
  const [blogNews, setblogNews] = useState<Post[]>(BlogNewsData);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(
    initialBreaking.length > 0 && initialBreaking.length < breakingNewsCount,
  );
  const [hasAd, setHasAd] = useState(false);
  const [hasSidebar1Ad, setHasSidebar1Ad] = useState(false);

  // ── Newsletter modal (client-only) ────────────────────────────────────────
  useEffect(() => {
    try {
      if (localStorage.getItem("newsletterModalShown")) return;
      import("bootstrap").then((bootstrap) => {
        const el = document.getElementById("newsletterModal");
        if (el) new bootstrap.Modal(el).show();
      });
    } catch {
      // Silently ignore — localStorage may be blocked in privacy mode
    }
  }, []);

  // ── Load More (the ONLY client-side fetch) ────────────────────────────────
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = page + 1;
    setPage(nextPage);
    setLoadingMore(true);

    try {
      // ✅ This correctly calls the Next.js API route from the client browser
      const res = await fetch(
        `/api/breakingNews-data?page=${nextPage}&limit=16`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data: BreakingData = await res.json();
      const newPosts = safeArray(data?.breakingNews);
      const total = data?.breakingNewsCount ?? breakingNewsCount;

      setBreakingNews((prev) => {
        const merged = [...prev, ...newPosts];
        if (merged.length >= total) setHasMore(false);
        return merged;
      });
    } catch (err) {
      console.error("Load more failed:", err);
      // Don't hide button on error — user can retry
    } finally {
      setLoadingMore(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Main container ─────────────────────────────────────────────────── */}
      <div
        className="gh-container pt-5 mt-5"
        style={{ color: "var(--foreground)" }}
      >
        <AdSpace
          slot="index_top"
          viewport="auto"
          initialAds={initialAds}
          onRender={(rendered) => setHasAd(rendered)}
        />

        <div className="row g-md-5">
          {/* ── LEFT COLUMN ──────────────────────────────────────────────── */}
          <div className="col-12 col-xl-8">
            {/* FeaturedSlider — SSR data, renders immediately */}
            {sliderPosts.length > 0 && (
              <div className="mb-5">
                <FeaturedSlider featuredPosts={sliderPosts.slice(0, 5)} />
              </div>
            )}

            {/* Breaking News Cards Grid */}
            <div className="row row-gap-md-3 mt-md-2">
              {breakingNews.length === 0
                ? // Show skeletons only if SSR also returned empty (API was down)
                  Array.from({ length: 4 }).map((_, i) => (
                    <div className="col-12 col-md-6" key={i}>
                      <PostSkeleton />
                    </div>
                  ))
                : breakingNews.map((item) => (
                    <div
                      className="col-12 col-md-6 card-container"
                      key={item.id}
                    >
                      <CommonCard
                        bodyClassName="px-0"
                        titleClassName="section-card"
                        authorClassName="gh-card-authors"
                        authorSlugClassName="authors-slug"
                        authorName={item.author}
                        publishDate={item.date}
                        titleText={item.title}
                        category_name={item.category_name ?? ""}
                        category_slug={item.category_slug ?? ""}
                        category_color={item.category_color ?? ""}
                        sub_category_name={item.sub_category_name ?? ""}
                        sub_category_slug={item.sub_category_slug ?? ""}
                        sub_category_color={item.sub_category_color ?? ""}
                        imgSrc={resolveImage(item.image)}
                        cardLink={`/${item.url}`}
                        authorSlug={item.author_slug || ""}
                      />
                    </div>
                  ))}

              {/* Load More */}
              <div className="text-center mt-md-2 px-md-3 px-2">
                {loadingMore && (
                  <div className="row g-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div className="col-12 col-md-6" key={i}>
                        <PostSkeleton />
                      </div>
                    ))}
                  </div>
                )}
                {!loadingMore && hasMore && (
                  <div className="d-flex justify-content-center mt-2 mb-4">
                    <button
                      className="newsletter-submit-loading-button"
                      onClick={handleLoadMore}
                    >
                      Load More
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── RIGHT COLUMN ─────────────────────────────────────────────── */}
          <div className="col-12 col-xl-4 ps-md-4 pe-md-5 px-2 pt-3">
            <AdSpace
              slot="sidebar_1"
              viewport="auto"
              initialAds={initialAds}
              onRender={(rendered) => setHasAd(rendered)}
            />
            {hasSidebar1Ad && <hr />}
            <SideBarTopCard />
            <hr />
            <div className="overflow-visible ">
              <CryptoSlider />
            </div>
            <div className="mb-5">
              <Sidebar initialData={sidebarData} />
            </div>
            <div className="overflow-visible mb-5">
              <ScamNews news={scamsNews} />
            </div>
            <div className="mb-5">
              <ExchangeAndWallets picks={PICKS} />
            </div>
            <div className="mb-5">
              <Subscribe />
            </div>
            <AdSpace
              slot="sidebar_2"
              viewport="auto"
              initialAds={initialAds}
              onRender={(rendered) => setHasAd(rendered)}
            />
          </div>
        </div>
      </div>
      {/* ── AI Section ─────────────────────────────────────────────────────── */}
      {blogNews.length > 0 && (
        <div style={{ minHeight: "157.5px" }}>
          <BlogNews post={blogNews} />
        </div>
      )}
      {/* ── Breaking News Slider ────────────────────────────────────────────── */}
      {breakingNews.length > 0 && (
        <div style={{ minHeight: "157.5px" }}>
          <BreakingNews post={breakingNews} />
        </div>
      )}

      {/* ── AI Section ─────────────────────────────────────────────────────── */}
      {AiNews.length > 0 && (
        <div style={{ minHeight: "157.5px" }}>
          <AI post={AiNews} />
        </div>
      )}

      {/* ── Featured Articles ───────────────────────────────────────────────── */}
      {featuredPosts.length > 0 && (
        <div className="p-md-3 gap-md-4" style={{ minHeight: "450px" }}>
          <div className="gh-container px-4">
            <FeaturedArticles featuredPosts={featuredPosts} />
          </div>
        </div>
      )}

      {/* ── Press Release ───────────────────────────────────────────────────── */}
      {PressReleaseNews.length > 0 && (
        <div style={{ minHeight: "157.5px" }}>
          <PressRelease post={PressReleaseNews} />
        </div>
      )}

      <NewsLetter />

      <div className="mt-3 mt-md-5">
        <AdSpace
          slot="index_bottom"
          viewport="auto"
          initialAds={initialAds}
          onRender={(rendered) => setHasAd(rendered)}
        />
      </div>

      <SocialMediaSection socialLinks={safeArray(socialLinks)} />
      <SubscribeEmailModal />
    </>
  );
}
